import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Switch, Alert, Tabs, Button, Space, message, Checkbox, Card, Divider, Collapse, Input as AntInput } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Search } = AntInput;

const MetricSettingsForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [monthlyMetrics, setMonthlyMetrics] = useState([]);
  const [applyToAllBrands, setApplyToAllBrands] = useState(false);
  const [massFormula, setMassFormula] = useState('');
  const [massDependentMetrics, setMassDependentMetrics] = useState([]);
  const [massLinkedMonthlyMetric, setMassLinkedMonthlyMetric] = useState(null);
  const [applyMonthlyMetricToAll, setApplyMonthlyMetricToAll] = useState(false);
  const [commonMetrics, setCommonMetrics] = useState([]);
  const [brandFilter, setBrandFilter] = useState('');
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [addToAllBrands, setAddToAllBrands] = useState(true);

  // Aylık metrikleri getiren fonksiyon
  const fetchMonthlyMetrics = useCallback(async () => {
    try {
      // Minimal mod ile sadece liste görünümü için gerekli verileri getir
      const response = await api.get('/monthly-metrics', {
        params: { minimal: 'true' }
      });
      if (response.data && Array.isArray(response.data)) {
        setMonthlyMetrics(response.data.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Aylık metrikler yüklenirken hata:', error);
      message.error('Aylık metrikler yüklenirken bir hata oluştu');
    }
  }, []);

  const fetchMetricData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Metrik detaylarını getir
      const response = await api.get(`/metrics/${id}`);
      
      if (!response.data) {
        message.error('Metrik bilgileri yüklenemedi');
        setLoading(false);
        return;
      }
      
      console.log('Alınan metrik verisi:', response.data);
      
      // Tüm markaların metrik detaylarını tek bir istekte getir
      const brandIds = brands.map(brand => brand.id).join(',');
      const detailsResponse = await api.get(`/metrics/${id}/brands`, {
        params: { brandIds }
      });
      
      if (!detailsResponse.data || !detailsResponse.data.brands) {
        message.error('Marka metrik detayları yüklenemedi');
        setLoading(false);
        return;
      }
      
      // Marka metrik detaylarını işle
      const brandDetailsMap = {};
      detailsResponse.data.brands.forEach(detail => {
        brandDetailsMap[detail.brandId] = detail;
      });
      
      // Her marka için metrik detaylarını oluştur
      const brandFormulas = brands.map(brand => {
        const detail = brandDetailsMap[brand.id] || {};
        
        // Metrik detaylarını kontrol et
        const hasFormula = !!detail.formula;
        const hasDependentMetrics = Array.isArray(detail.dependentMetrics) && 
                                    detail.dependentMetrics.length > 0;
        
        // useAutoCalculation değerini belirle
        const useAutoCalculation = response.data.isAutoCalculated && 
                                  (hasFormula || hasDependentMetrics) &&
                                  detail.isActive;
        
        return {
          brandId: brand.id,
          formula: detail.formula || '',
          dependentMetrics: detail.dependentMetrics || [],
          linkedMonthlyMetricId: detail.linkedMonthlyMetricId || null,
          useAutoCalculation
        };
      });

      // Form değerlerini ayarla
      form.setFieldsValue({
        name: response.data.name,
        description: response.data.description,
        internalNotes: response.data.internalNotes,
        dataType: response.data.dataType,
        isAutoCalculated: response.data.isAutoCalculated,
        brandFormulas: brandFormulas,
        color: response.data.color || '#FFFFFF'
      });

      console.log('Form değerleri ayarlandı');
    } catch (error) {
      console.error('Metrik bilgileri yüklenirken hata:', error);
      message.error('Metrik bilgileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [id, brands.length, form]);

  const fetchBrands = useCallback(async () => {
    try {
      // Önce minimal modda markaları getir
      const response = await api.get('/brands', {
        params: {
          minimal: 'true'
        }
      });
      
      if (!response.data || !Array.isArray(response.data)) {
        message.error('Markalar yüklenirken bir hata oluştu');
        return;
      }

      const sortedBrands = response.data
        .filter(brand => brand && brand.name)
        .sort((a, b) => a.name.localeCompare(b.name));

      setBrands(sortedBrands);
      
      // Ortak metrikler için ilk markayı kullan
      if (sortedBrands.length > 0) {
        loadCommonMetrics(sortedBrands[0].id);
      }
    } catch (error) {
      console.error('Marka yükleme hatası:', error);
      message.error('Markalar yüklenirken bir hata oluştu');
    }
  }, []);
  
  // Ortak metrikler için ilk markayı kullan
  const loadCommonMetrics = async (brandId) => {
    try {
      const response = await api.get(`/brands/${brandId}/metrics`);
      if (response.data && Array.isArray(response.data)) {
        const metrics = response.data
          .filter(m => m.BrandMetric?.isActive)
          .map(m => ({
            id: m.id,
            name: m.name,
            type: 'daily'
          }));
        setCommonMetrics(metrics);
      }
    } catch (error) {
      console.error('Ortak metrikler yüklenirken hata:', error);
    }
  };

  // Marka metriklerini yükle - ihtiyaç duyulduğunda
  const loadBrandMetrics = useCallback(async (brandId) => {
    try {
      // Marka metriklerini getir
      const response = await api.get(`/brands/${brandId}/metrics`);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Beklenmeyen API yanıtı:', response.data);
        return [];
      }
      
      return response.data;
    } catch (error) {
      console.error(`${brandId} ID'li marka için metrikler yüklenirken hata:`, error);
      return [];
    }
  }, []);

  useEffect(() => {
    // İlk yükleme için
    fetchBrands();
    fetchMonthlyMetrics();
    
    // Düzenleme sayfası değilse formu sıfırla
    if (!id) {
      form.resetFields();
    }
    // Bu effect sadece bir kez çalışmalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && brands.length > 0) {
      // Düzenleme sayfası için metrik verilerini yükle
      fetchMetricData();
    }
    // brands.length değiştiğinde veya id değiştiğinde çalışsın
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, brands.length]);

  const getAvailableMetrics = (currentMetricId, brandId) => {
    const brand = brands.find(b => b.id === brandId);
    if (!brand) return [];

    // Eğer brand.Metrics yoksa, lazy loading ile yükle
    if (!brand.Metrics) {
      // Asenkron olarak metrikleri yükle
      // Burada state güncellemesi yapmak yerine sadece bir kez yükleme yapıyoruz
      // ve sonucu önbelleğe alıyoruz
      if (!brand._metricsLoading) {
        // Yükleme işlemini takip etmek için özel bir bayrak kullan
        brand._metricsLoading = true;
        
        loadBrandMetrics(brandId).then(metrics => {
          // Brands state'ini güncelle, ama sadece bir kez
          setBrands(prevBrands => {
            // Eğer bu marka için metrikler zaten yüklendiyse tekrar güncelleme yapma
            const targetBrand = prevBrands.find(b => b.id === brandId);
            if (targetBrand && targetBrand.Metrics) return prevBrands;
            
            return prevBrands.map(b => 
              b.id === brandId 
                ? { ...b, Metrics: metrics, _metricsLoading: false } 
                : b
            );
          });
        }).catch(() => {
          // Hata durumunda state güncelleme yapma
          setBrands(prevBrands => 
            prevBrands.map(b => 
              b.id === brandId 
                ? { ...b, _metricsLoading: false } 
                : b
            )
          );
        });
      }
      return []; // İlk render için boş dizi döndür
    }

    // Sadece günlük metrikleri al, aylık metrikleri dahil etme
    const dailyMetrics = (brand.Metrics || [])
      .filter(m => 
        !m.isDeleted && 
        m.id !== currentMetricId && 
        m.BrandMetric?.isActive
      )
      .map(m => ({
        id: m.id,
        name: m.name,
        type: 'daily'
      }));

    return dailyMetrics.sort((a, b) => a.name.localeCompare(b.name));
  };
  
  // Toplu formül değişikliği
  const handleMassFormulaChange = (e) => {
    const formula = e.target.value;
    setMassFormula(formula);
    
    if (applyToAllBrands) {
      // Form değerlerini al
      const formValues = form.getFieldsValue();
      const brandFormulas = formValues.brandFormulas || [];
      
      // Önce mevcut form değerlerini al ki ilişkili aylık metrikler korunsun
      const currentFormValues = form.getFieldValue('brandFormulas') || [];
      
      // Tüm markalara aynı formülü uygula
      // Diğer alanları koruyarak güncelle
      const updatedBrandFormulas = brandFormulas.map((bf, index) => {
        // İlişkili aylık metrik değerini mevcut form değerlerinden al
        const currentBf = currentFormValues[index] || {};
        const linkedMonthlyMetricId = currentBf.linkedMonthlyMetricId || bf.linkedMonthlyMetricId;
        
        return {
          ...bf, // Önceki tüm değerleri koru
          formula: formula,
          // İlişkili aylık metrik değerini açıkça koru
          linkedMonthlyMetricId: linkedMonthlyMetricId
        };
      });
      
      // Form değerlerini güncelle
      form.setFieldsValue({
        ...formValues,
        brandFormulas: updatedBrandFormulas
      });
    }
  };
  
  // Toplu bağımlı metrik değişikliği
  const handleMassDependentMetricsChange = (selectedMetrics) => {
    setMassDependentMetrics(selectedMetrics);
    
    if (applyToAllBrands) {
      // Form değerlerini al
      const formValues = form.getFieldsValue();
      const brandFormulas = formValues.brandFormulas || [];
      
      // Önce mevcut form değerlerini al ki ilişkili aylık metrikler korunsun
      const currentFormValues = form.getFieldValue('brandFormulas') || [];
      
      // Tüm markalara aynı bağımlı metrikleri uygula
      // Diğer alanları koruyarak güncelle
      const updatedBrandFormulas = brandFormulas.map((bf, index) => {
        // İlişkili aylık metrik değerini mevcut form değerlerinden al
        const currentBf = currentFormValues[index] || {};
        const linkedMonthlyMetricId = currentBf.linkedMonthlyMetricId || bf.linkedMonthlyMetricId;
        
        return {
          ...bf, // Önceki tüm değerleri koru
          dependentMetrics: selectedMetrics,
          // İlişkili aylık metrik değerini açıkça koru
          linkedMonthlyMetricId: linkedMonthlyMetricId
        };
      });
      
      // Form değerlerini güncelle
      form.setFieldsValue({
        ...formValues,
        brandFormulas: updatedBrandFormulas
      });
    }
  };
  
  // Tüm markalara uygulama durumu değiştiğinde
  const handleApplyToAllChange = (e) => {
    const checked = e.target.checked;
    setApplyToAllBrands(checked);
    
    // // Checkbox işaretlendiğinde otomatik uygulama kaldırıldı.
    // if (checked) {
    //   // Tüm markalara mevcut toplu değerleri uygula
    //   const formValues = form.getFieldsValue();
    //   const brandFormulas = formValues.brandFormulas || [];
      
    //   const updatedBrandFormulas = brandFormulas.map(bf => ({
    //     ...bf,
    //     formula: massFormula,
    //     dependentMetrics: massDependentMetrics
    //   }));
      
    //   form.setFieldsValue({
    //     ...formValues,
    //     brandFormulas: updatedBrandFormulas
    //   });
    // }
  };

  // Toplu aylık metrik değişikliği
  const handleMassLinkedMonthlyMetricChange = (value) => {
    setMassLinkedMonthlyMetric(value);
    
    if (applyMonthlyMetricToAll) {
      // Form değerlerini al
      const formValues = form.getFieldsValue();
      const brandFormulas = formValues.brandFormulas || [];
      
      // Tüm markalara aynı ilişkili aylık metriği uygula
      // Diğer alanları koruyarak güncelle
      const updatedBrandFormulas = brandFormulas.map(bf => ({
        ...bf, // Önceki tüm değerleri koru
        linkedMonthlyMetricId: value
      }));
      
      // Form değerlerini güncelle
      form.setFieldsValue({
        ...formValues,
        brandFormulas: updatedBrandFormulas
      });
    }
  };

  // İlişkili aylık metriği tüm markalara uygulama durumu değiştiğinde
  const handleApplyMonthlyMetricToAllChange = (e) => {
    const checked = e.target.checked;
    setApplyMonthlyMetricToAll(checked);
    
    // // Checkbox işaretlendiğinde otomatik uygulama kaldırıldı.
    // if (checked) {
    //   // Tüm markalara mevcut ilişkili aylık metriği uygula
    //   const formValues = form.getFieldsValue();
    //   const brandFormulas = formValues.brandFormulas || [];
      
    //   const updatedBrandFormulas = brandFormulas.map(bf => ({
    //     ...bf,
    //     linkedMonthlyMetricId: massLinkedMonthlyMetric
    //   }));
      
    //   form.setFieldsValue({
    //     ...formValues,
    //     brandFormulas: updatedBrandFormulas
    //   });
    // }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const { name, description, internalNotes, dataType, isAutoCalculated, brandFormulas = [], color } = values;

      console.log('Form değerleri:', values);
      
      // Sadece temel metrik güncellemesi mi kontrolü
      const isBasicUpdate = id && !isAutoCalculated && !applyToAllBrands && !applyMonthlyMetricToAll;
      
      let submitData = {
        name,
        description,
        internalNotes,
        dataType,
        isAutoCalculated,
        color: color || '#FFFFFF'
      };
      
      // Eğer temel güncelleme değilse, marka bilgilerini işle
      if (!isBasicUpdate) {
        // Tanımlı olmayan öğeleri filtrele
        const validBrandFormulas = brandFormulas.filter(bf => bf !== undefined && bf !== null);
        
        // Tüm markaları işle
        let processedBrands = [];
        
        if (applyToAllBrands || applyMonthlyMetricToAll) {
          // Tüm markalar için işlem yapılacaksa
          processedBrands = brands.map(brand => {
            // Mevcut form verilerinden bu markanın değerlerini bul
            const existingBrandFormula = validBrandFormulas.find(bf => bf.brandId === brand.id);
            
            const brandData = {
              brandId: brand.id
            };
            
            // İlişkili aylık metrik için:
            // Eğer applyMonthlyMetricToAll seçeneği işaretliyse, seçilen metriği kullan
            // Değilse ve mevcut bir değer varsa, mevcut değeri koru
            if (applyMonthlyMetricToAll) {
              brandData.linkedMonthlyMetricId = massLinkedMonthlyMetric;
            } else if (existingBrandFormula && existingBrandFormula.linkedMonthlyMetricId !== undefined) {
              // Mevcut değeri koru, yoksa hiç bu alanı gönderme (backend'deki değer korunacak)
              brandData.linkedMonthlyMetricId = existingBrandFormula.linkedMonthlyMetricId;
            }
            
            // Eğer otomatik hesaplama kullanılıyorsa formül ve bağımlı metrikleri ekle
            if (isAutoCalculated && applyToAllBrands) {
              brandData.formula = massFormula || null;
              brandData.dependentMetrics = massDependentMetrics || [];
            } else if (isAutoCalculated) {
              // Otomatik hesaplama varsa ama toplu formül uygulanmayacaksa
              // Form'dan mevcut değerleri bul
              const brandFormula = validBrandFormulas.find(bf => bf.brandId === brand.id);
              if (brandFormula && brandFormula.useAutoCalculation) {
                brandData.formula = brandFormula.formula || null;
                brandData.dependentMetrics = brandFormula.dependentMetrics || [];
              }
            }
            
            return brandData;
          });
        } else {
          // Form'dan gelen değerleri kullan
          processedBrands = validBrandFormulas.map((bf, index) => {
            // brandId değeri yoksa brands dizisinden al
            const brandId = bf.brandId || (brands[index] ? brands[index].id : null);
            
            if (!brandId) {
              console.error(`Marka ID'si bulunamadı, index: ${index}`, bf);
              return null;
            }

            const brandData = {
              brandId: brandId,
              linkedMonthlyMetricId: bf.linkedMonthlyMetricId || null
            };

            // Eğer otomatik hesaplama kullanılıyorsa formül ve bağımlı metrikleri ekle
            if (isAutoCalculated && bf.useAutoCalculation === true) {
              brandData.formula = bf.formula || null;
              brandData.dependentMetrics = bf.dependentMetrics || [];
            }

            return brandData;
          }).filter(brand => brand !== null); // null değerleri filtrele
        }
        
        // Eğer hiç marka yoksa ve yeni metrik ekleniyorsa ve tüm markalara eklenecekse
        if (processedBrands.length === 0 && !id && addToAllBrands) {
          // Yeni metrik oluşturulurken ve tüm markalara eklenecekse, boş bir dizi göndermek yeterli
          // Backend tarafında tüm markalar bulunup eklenecek
          processedBrands = [{ 
            brandId: 'placeholder', // Bu değer backend tarafında kullanılmayacak
            linkedMonthlyMetricId: applyMonthlyMetricToAll ? massLinkedMonthlyMetric : null,
            formula: null,
            dependentMetrics: []
          }];
        } 
        // Eğer hiç marka yoksa ve yukarıdaki koşul sağlanmıyorsa hata göster
        else if (processedBrands.length === 0) {
          throw new Error('Geçerli marka bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
        }

        // Marka bilgilerini ekle
        submitData.brands = processedBrands;
        submitData.addToAllBrands = !id && addToAllBrands; // Sadece yeni metrik eklenirken ve seçenek işaretliyse true
      }
      
      console.log('Gönderilecek veri:', submitData);

      if (id) {
        console.log(`Metrik güncelleniyor (ID: ${id})...`);
        const response = await api.put(`/metrics/${id}`, submitData);
        console.log('Güncelleme yanıtı:', response.data);
        message.success('Metrik güncellendi');
      } else {
        console.log('Yeni metrik oluşturuluyor...');
        const response = await api.post('/metrics', submitData);
        console.log('Oluşturma yanıtı:', response.data);
        message.success('Metrik eklendi');
      }
      
      navigate('/metrics');
    } catch (error) {
      console.error('Metrik kaydetme hatası:', error);
      
      if (error.response?.data?.error) {
        message.error({
          content: error.response.data.error,
          duration: 5,
          style: {
            marginTop: '20vh',
          },
        });
      } else {
        message.error('Metrik kaydedilirken bir hata oluştu: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrelenmiş markaları getir
  const getFilteredBrands = () => {
    if (!brandFilter) return brands;
    
    return brands.filter(brand => 
      brand.name.toLowerCase().includes(brandFilter.toLowerCase())
    );
  };
  
  // Tab değişikliği
  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>{id ? 'Metrik Düzenle' : 'Yeni Metrik Ekle'}</h2>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          dataType: 'number',
          isAutoCalculated: false,
          brandFormulas: brands.map(brand => ({
            brandId: brand.id,
            formula: '',
            dependentMetrics: [],
            linkedMonthlyMetricId: null,
            useAutoCalculation: false
          }))
        }}
      >
        <Tabs activeKey={activeTabKey} onChange={handleTabChange}>
          <Tabs.TabPane tab="Temel Bilgiler" key="1">
            <Form.Item
              name="name"
              label="Metrik Adı"
              rules={[{ required: true, message: 'Lütfen metrik adını girin' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label="Açıklama"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="internalNotes"
              label="Dahili Notlar"
              tooltip="Bu notlar tooltip'te görünür"
            >
              <TextArea 
                rows={3} 
                placeholder="Dahili notlar" 
                maxLength={1000}
              />
            </Form.Item>

            <Form.Item
              name="dataType"
              label="Veri Tipi"
              rules={[{ required: true, message: 'Lütfen veri tipini seçin' }]}
            >
              <Select>
                <Option value="number">Sayı</Option>
                <Option value="currency">Para Birimi</Option>
                <Option value="percentage">Yüzde</Option>
                <Option value="text">Metin</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="isAutoCalculated"
              valuePropName="checked"
              label="Otomatik Hesaplama"
            >
              <Switch />
            </Form.Item>
            
            <Form.Item
              name="color"
              label="Metrik Rengi"
              tooltip="Metriğin görüntülenmesinde kullanılacak renk"
            >
              <Input
                type="color"
                style={{ width: 60, height: 32, padding: 0, cursor: 'pointer' }}
              />
            </Form.Item>
            
            {!id && (
              <Form.Item
                label="Tüm Markalara Ekle"
                tooltip="İşaretlenirse, bu metrik tüm mevcut markalara otomatik olarak eklenecektir"
              >
                <Checkbox 
                  checked={addToAllBrands}
                  onChange={(e) => setAddToAllBrands(e.target.checked)}
                >
                  Bu metriği tüm mevcut markalara ekle
                </Checkbox>
              </Form.Item>
            )}
            
            {form.getFieldValue('isAutoCalculated') && (
              <Card title="Toplu Formül ve Bağımlı Metrik Ayarları" style={{ marginBottom: 20 }}>
                <Form.Item>
                  <Checkbox 
                    checked={applyToAllBrands}
                    onChange={handleApplyToAllChange}
                  >
                    Tüm Markalara Aynı Formül ve Bağımlı Metrikleri Uygula
                  </Checkbox>
                </Form.Item>
                
                {applyToAllBrands && (
                  <>
                    <Form.Item label="Formül">
                      <TextArea 
                        value={massFormula}
                        onChange={handleMassFormulaChange}
                        placeholder="Tüm markalar için formül"
                        rows={3}
                      />
                    </Form.Item>
                    
                    <Form.Item label="Bağımlı Metrikler">
                      <Select
                        mode="multiple"
                        placeholder="Bağımlı metrikler seçin"
                        value={massDependentMetrics}
                        onChange={handleMassDependentMetricsChange}
                        style={{ width: '100%' }}
                        optionFilterProp="children"
                      >
                        {commonMetrics.map(metric => (
                          <Option key={metric.id} value={metric.id}>
                            {metric.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                )}
              </Card>
            )}
            
            {/* İlişkili Aylık Metrik için Toplu Ayarlar kartı */}
            <Card title="İlişkili Aylık Metrik Ayarları" style={{ marginBottom: 20 }}>
              <Form.Item>
                <Checkbox 
                  checked={applyMonthlyMetricToAll}
                  onChange={handleApplyMonthlyMetricToAllChange}
                >
                  Tüm Markalara Aynı İlişkili Aylık Metriği Uygula
                </Checkbox>
              </Form.Item>
              
              {applyMonthlyMetricToAll && (
                <Form.Item label="İlişkili Aylık Metrik">
                  <Select
                    allowClear
                    placeholder="İlişkili aylık metrik seçin (opsiyonel)"
                    value={massLinkedMonthlyMetric}
                    onChange={handleMassLinkedMonthlyMetricChange}
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                  >
                    {monthlyMetrics.map(metric => (
                      <Option key={metric.id} value={metric.id}>
                        {metric.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Card>
          </Tabs.TabPane>
          
          {!applyToAllBrands && (
            <Tabs.TabPane tab="Markalar" key="2">
              <Alert
                message="Not"
                description="Çok sayıda marka olduğu için, aşağıdaki arama kutusunu kullanarak markaları filtreleyebilirsiniz."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Search
                placeholder="Marka ara..."
                value={brandFilter}
                onChange={e => setBrandFilter(e.target.value)}
                style={{ marginBottom: 16, width: 300 }}
                allowClear
              />
              
              <Collapse>
                {getFilteredBrands().map((brand, index) => (
                  <Panel header={brand.name} key={brand.id}>
                    <Form.Item
                      name={['brandFormulas', index, 'brandId']}
                      hidden
                      initialValue={brand.id}
                    >
                      <Input type="hidden" />
                    </Form.Item>
                    
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) => 
                        prevValues.isAutoCalculated !== currentValues.isAutoCalculated
                      }
                    >
                      {({ getFieldValue }) => {
                        return getFieldValue('isAutoCalculated') && (
                          <>
                            <Form.Item
                              name={['brandFormulas', index, 'useAutoCalculation']}
                              valuePropName="checked"
                              label="Bu Marka İçin Otomatik Hesaplama Kullan"
                            >
                              <Switch />
                            </Form.Item>
                            
                            <Form.Item
                              shouldUpdate={(prevValues, currentValues) => {
                                const prevUseAuto = prevValues.brandFormulas?.[index]?.useAutoCalculation;
                                const currentUseAuto = currentValues.brandFormulas?.[index]?.useAutoCalculation;
                                return prevUseAuto !== currentUseAuto;
                              }}
                            >
                              {({ getFieldValue: getNestedFieldValue }) => {
                                const useAutoCalculation = getNestedFieldValue(['brandFormulas', index, 'useAutoCalculation']);
                                return useAutoCalculation && (
                                  <>
                                    <Form.Item
                                      name={['brandFormulas', index, 'formula']}
                                      label="Formül"
                                    >
                                      <TextArea 
                                        rows={3}
                                        placeholder="Formül girin (örn: metrik_1 + metrik_2 * 0.1)"
                                      />
                                    </Form.Item>
                                    
                                    <Form.Item
                                      name={['brandFormulas', index, 'dependentMetrics']}
                                      label="Bağımlı Metrikler"
                                    >
                                      <Select
                                        mode="multiple"
                                        placeholder="Bağımlı metrikler seçin"
                                        style={{ width: '100%' }}
                                        optionFilterProp="children"
                                        showSearch
                                        filterOption={(input, option) =>
                                          option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                      >
                                        {getAvailableMetrics(id, brand.id).map(metric => (
                                          <Option key={metric.id} value={metric.id}>
                                            {metric.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  </>
                                );
                              }}
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.Item>
                    
                    {!applyMonthlyMetricToAll && (
                      <Form.Item
                        name={['brandFormulas', index, 'linkedMonthlyMetricId']}
                        label="İlişkili Aylık Metrik"
                      >
                        <Select
                          allowClear
                          placeholder="İlişkili aylık metrik seçin (opsiyonel)"
                          style={{ width: '100%' }}
                          optionFilterProp="children"
                        >
                          {monthlyMetrics.map(metric => (
                            <Option key={metric.id} value={metric.id}>
                              {metric.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Panel>
                ))}
              </Collapse>
            </Tabs.TabPane>
          )}
        </Tabs>
        
        <Divider />
        
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button onClick={() => navigate('/metrics')}>
              İptal
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default MetricSettingsForm; 